import React, { useEffect, useState } from "react";
import GenerationLoader from "./GenerationLoader";
import { CTA_OPTIONS } from "../utils/prompts";

const STYLE_CONFIGS = {
  minimal: {
    bg: "#ffffff",
    accent: "#3f73f9",
    text: "#1a1a2e",
    subtext: "#666",
    btnBg: "#3f73f9",
    btnText: "#fff",
    badge: "Минималистичный",
    badgeColor: "#3f73f9",
  },
  vivid: {
    bg: "linear-gradient(135deg, #ff6b35 0%, #f7c59f 100%)",
    accent: "#fff",
    text: "#fff",
    subtext: "rgba(255,255,255,0.85)",
    btnBg: "#fff",
    btnText: "#ff6b35",
    badge: "Яркий",
    badgeColor: "#ff6b35",
  },
  premium: {
    bg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    accent: "#c9a84c",
    text: "#fff",
    subtext: "rgba(201,168,76,0.85)",
    btnBg: "#c9a84c",
    btnText: "#1a1a2e",
    badge: "Премиум",
    badgeColor: "#c9a84c",
  },
};

function MockBanner({ variant, formData, isSelected, onSelect }) {
  const config = STYLE_CONFIGS[variant.style] || STYLE_CONFIGS.minimal;
  const ctaLabel = CTA_OPTIONS.find((o) => o.value === formData?.ctaButton)?.label;
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    setImgBroken(false);
  }, [variant.imageUrl]);

  const showRemoteImage = Boolean(variant.imageUrl) && !imgBroken;

  return (
    <div
      className={`aic-banner-card ${isSelected ? "aic-banner-card--selected" : ""}`}
      onClick={onSelect}
    >
      <div className="aic-banner-card__badge" style={{ background: config.badgeColor }}>
        {config.badge}
      </div>

      {isSelected && (
        <div className="aic-banner-card__check">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="8" fill="#3f73f9"/>
            <path d="M4 8l3 3 5-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      <div
        className="aic-banner-mock"
        style={{ background: config.bg }}
      >
        {showRemoteImage ? (
          <img
            src={variant.imageUrl}
            alt={variant.label}
            className="aic-banner-mock__image"
            onError={() => setImgBroken(true)}
          />
        ) : (
          <div className="aic-banner-mock__content">
            <div
              className="aic-banner-mock__brand"
              style={{ color: config.accent }}
            >
              {formData?.brandName || "Бренд"}
            </div>
            <div
              className="aic-banner-mock__headline"
              style={{ color: config.text }}
            >
              {formData?.headline || "Заголовок вашего баннера"}
            </div>
            <div
              className="aic-banner-mock__offer"
              style={{ color: config.subtext }}
            >
              {formData?.offer?.slice(0, 60) || "Описание предложения"}
            </div>
            {ctaLabel && ctaLabel !== "Без кнопки" && (
              <button
                className="aic-banner-mock__cta"
                style={{ background: config.btnBg, color: config.btnText }}
              >
                {ctaLabel}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="aic-banner-card__footer">
        <span className="aic-banner-card__style">{variant.label}</span>
        <span className="aic-banner-card__select-hint">
          {isSelected ? "Выбран" : "Нажмите для выбора"}
        </span>
      </div>
    </div>
  );
}

function EmptyPreview() {
  return (
    <div className="aic-preview-empty">
      <div className="aic-preview-empty__icon">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <rect width="64" height="64" rx="16" fill="#f0f4ff"/>
          <rect x="12" y="20" width="40" height="24" rx="4" fill="#dce8ff"/>
          <rect x="16" y="24" width="16" height="3" rx="1.5" fill="#3f73f9" opacity="0.5"/>
          <rect x="16" y="30" width="28" height="2" rx="1" fill="#3f73f9" opacity="0.3"/>
          <rect x="16" y="34" width="20" height="2" rx="1" fill="#3f73f9" opacity="0.2"/>
          <rect x="36" y="32" width="12" height="8" rx="2" fill="#3f73f9" opacity="0.4"/>
        </svg>
      </div>
      <h4 className="aic-preview-empty__title">Здесь появятся ваши баннеры</h4>
      <p className="aic-preview-empty__text">
        Заполните форму слева и нажмите<br />«Сгенерировать» — ИИ создаст 3 варианта
      </p>
    </div>
  );
}

function BannerPreview({ isLoading, currentStep, variants, selectedId, onSelect, onGetResizes, formData }) {
  if (isLoading) {
    return (
      <div className="aic-preview">
        <GenerationLoader currentStep={currentStep} />
      </div>
    );
  }

  if (!variants || variants.length === 0) {
    return (
      <div className="aic-preview">
        <EmptyPreview />
      </div>
    );
  }

  return (
    <div className="aic-preview">
      <div className="aic-preview__header">
        <h3 className="aic-preview__title">Выберите вариант дизайна</h3>
        <p className="aic-preview__subtitle">Нажмите на понравившийся баннер, затем получите ресайзы</p>
      </div>

      <div className="aic-preview__variants">
        {variants.map((v) => (
          <MockBanner
            key={v.id}
            variant={v}
            formData={formData}
            isSelected={selectedId === v.id}
            onSelect={() => onSelect(v.id)}
          />
        ))}
      </div>

      {selectedId && (
        <div className="aic-preview__actions">
          <button className="aic-btn aic-btn--primary aic-btn--lg" onClick={onGetResizes}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
              <rect x="13" y="3" width="8" height="5" rx="1" stroke="currentColor" strokeWidth="2"/>
              <rect x="3" y="13" width="8" height="5" rx="1" stroke="currentColor" strokeWidth="2"/>
              <rect x="13" y="10" width="8" height="11" rx="1" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Получить ресайзы
          </button>
          <p className="aic-preview__actions-note">728×90, 300×250, 300×600</p>
        </div>
      )}
    </div>
  );
}

export default BannerPreview;
